import { useParams } from "@tanstack/react-router";
import type * as fhirTypes from "fhir/r4";
import { createContext, useContext, useMemo, useState } from "react";
import { DEFAULT_PAGE_SIZE, useFiles } from "./network/useFiles";

interface FilesContextValue {
  files: fhirTypes.DocumentReference[];
  isLoadingFiles: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  selectedCategory: string;
  setSelectedCategory: (value: string) => void;
  filteredFiles: fhirTypes.DocumentReference[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
}

export const DEFAULT_CATEGORY = "default";

const FilesContext = createContext<FilesContextValue | undefined>(undefined);

export const FilesProvider = ({ children }: { children: React.ReactNode }) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const {
    data: filesData,
    isLoading: isLoadingFiles,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    refetch,
  } = useFiles({
    patientId,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [selectedCategory, setSelectedCategory] =
    useState<string>(DEFAULT_CATEGORY);

  // Flatten all pages into a single array of files
  const allFiles = useMemo<fhirTypes.DocumentReference[]>(() => {
    if (!filesData?.pages) return [];
    return filesData.pages.flatMap((page) => page.files);
  }, [filesData?.pages]);

  const filteredFiles = useMemo<fhirTypes.DocumentReference[]>(() => {
    if (selectedCategory === DEFAULT_CATEGORY) return allFiles;
    return allFiles.filter((f) => {
      const code = f.type?.coding?.[0]?.code;
      return code === selectedCategory;
    });
  }, [allFiles, selectedCategory]);

  const value = useMemo<FilesContextValue>(
    () => ({
      files: allFiles,
      isLoadingFiles,
      isFetchingNextPage,
      isError,
      selectedCategory,
      setSelectedCategory,
      filteredFiles,
      hasNextPage,
      fetchNextPage,
      refetch,
    }),
    [
      allFiles,
      isLoadingFiles,
      isFetchingNextPage,
      isError,
      selectedCategory,
      filteredFiles,
      hasNextPage,
      fetchNextPage,
      refetch,
    ],
  );

  return (
    <FilesContext.Provider value={value}>{children}</FilesContext.Provider>
  );
};

export const useFilesContext = () => {
  const ctx = useContext(FilesContext);
  if (!ctx) {
    throw new Error("useFilesContext must be used within a FilesProvider");
  }
  return ctx;
};
